import { Link } from "react-router-dom";
import useScrollAnimation from "../../../hooks/useScrollAnimation";

const industries = [
  {
    name: "MED SPAS",
    tagline: "From missed calls to booked treatments.",
    description:
      "Capture every inquiry, qualify instantly, and fill your calendar—without lifting a finger. AI systems built for high-volume aesthetic clinics.",
    icon: "ri-sparkling-2-line",
  },
  {
    name: "CLINICAL PRACTICES",
    tagline: "Compliance-ready. Operationally bulletproof.",
    description:
      "Streamline patient intake, automate scheduling, and reduce administrative drag. Built around clinical workflow requirements.",
    icon: "ri-hospital-line",
  },
  {
    name: "WELLNESS BRANDS",
    tagline: "Turn first-timers into lifetime clients.",
    description:
      "Lifecycle automation that sustains recurring bookings and deepens patient relationships long after the first appointment.",
    icon: "ri-heart-pulse-line",
  },
];

const IndustriesSection = () => {
  const sectionRef = useScrollAnimation<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="bg-black py-24 md:py-36 px-6 md:px-16 border-t border-white/5 relative overflow-hidden"
    >
      {/* Top-right abstract accent */}
      <div
        className="absolute right-0 top-0 pointer-events-none"
        style={{
          width: 1,
          height: "100%",
          background: "linear-gradient(to bottom, transparent, rgba(255,209,0,0.08) 40%, rgba(255,209,0,0.08) 60%, transparent)",
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-16 reveal">
          <span
            className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            WE SERVE
          </span>
          <h2
            className="text-white font-black uppercase text-4xl md:text-5xl leading-none"
            style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
          >
            INDUSTRIES WE
            <br />
            BUILD FOR
          </h2>
        </div>

        {/* Horizontal Blocks */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {industries.map((ind, i) => (
            <div
              key={i}
              className={`reveal delay-${(i + 1) * 100} group p-10 md:p-12 flex flex-col gap-6 border-b lg:border-b-0 lg:border-r border-[#FFD100]/10 last:border-0 relative overflow-hidden transition-all duration-300 hover:bg-[#1A1A1A] ${
                i === 1 ? "bg-[#1A1A1A] hover:bg-[#222]" : "bg-black"
              }`}
            >
              {/* Ghost icon background */}
              <div
                className="absolute bottom-6 right-6 opacity-[0.04] group-hover:opacity-[0.07] transition-opacity duration-300"
                style={{ fontSize: "7rem" }}
              >
                <i className={ind.icon} style={{ color: "#FFD100" }} />
              </div>

              {/* Gold accent */}
              <div className="w-16 h-0.5 bg-[#FFD100]" />

              {/* Industry icon */}
              <div className="w-10 h-10 flex items-center justify-center border border-[#FFD100]/30 group-hover:border-[#FFD100]/60 transition-colors duration-300">
                <i className={`${ind.icon} text-[#FFD100] text-base`} />
              </div>

              {/* Industry name */}
              <h3
                className="text-white font-black text-3xl md:text-4xl tracking-tight leading-none"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {ind.name}
              </h3>

              {/* Tagline */}
              <p
                className="text-[#FFD100] text-sm font-bold tracking-[0.5px]"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {ind.tagline}
              </p>

              {/* Description */}
              <p
                className="text-[#E5E5E5] text-sm leading-relaxed flex-1"
                style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.8" }}
              >
                {ind.description}
              </p>

              {/* Link */}
              <Link
                to="/industries"
                className="text-[#FFD100] text-xs font-black tracking-[2px] flex items-center gap-2 group-hover:gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                EXPLORE <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
